import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

const skeletonStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .skeleton-box {
    display: inline-block;
    height: 1em;
    position: relative;
    overflow: hidden;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-repeat: no-repeat;
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
    border-radius: 4px;
    width: 100%;
  }
  .skeleton-circle {
    display: inline-block;
    height: 50px;
    width: 50px;
    border-radius: 8px;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
  }
  .img-zoom-hover {
    transition: transform 0.3s ease;
    cursor: zoom-in;
  }
  .img-zoom-hover:hover {
    transform: scale(1.02);
  }
`;

const toProperCase = (text = "") => {
  if (!text) return "";
  const lowerCaseWords = new Set([
    "of", "and", "or", "as", "in", "on", "at", "to", "for", "by",
    "with", "via", "vs", "etc", "the", "a", "an",
  ]);
  return text.toLowerCase().split(" ").map((word, i, arr) => {
    if (i !== 0 && i !== arr.length - 1 && lowerCaseWords.has(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(" ");
};

const normalize = (str) => (str || "").toString().toLowerCase().trim();

const EditAd = () => {
  const BASE_URL = "https://adera-web-api.azurewebsites.net/v1";
  const ITEMS_PER_PAGE = 100;

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);

  const [allLookups, setAllLookups] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [editTypes, setEditTypes] = useState([]);
  const [editAvailableSizes, setEditAvailableSizes] = useState([]);

  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("All");
  const [filters, setFilters] = useState({ partyType: "", adType: "" });
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });

  const [showPermitModal, setShowPermitModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const [permitData, setPermitData] = useState(null);
  const [viewData, setViewData] = useState(null);

  const [editForm, setEditForm] = useState({
    id: "",
    clientId: "",
    title: "",
    description: "",
    address: "",
    price: 0,
    status: "",
    measurement: 1,
    time: 1,
    lookUpId: "",
    zoneId: "",
    zoneName: "",
    categoryId: "",
    categoryName: "",
    typeId: "",
    typeName: "",
    sizeName: "",
    location: { lga: "", longitude: 0, latitude: 0, altitude: 0 },
    client: {
      type: "Individual",
      company: { name: "", address: "", phoneNumber: "", email: "", type: "" },
      individual: { firstName: "", lastName: "", gender: "", phoneNumber: "", email: "" },
    },
    imageFile: null,
    imagePreview: null,
  });

  const getToken = () => localStorage.getItem("token") || "";

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" })
      .format(amount || 0).replace("NGN", "₦");

  const formatDate = (dateString) =>
    !dateString ? "N/A" : new Date(dateString).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });

  const getExpiryDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    d.setFullYear(d.getFullYear() + 1);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  const getClientName = (client) => {
    if (!client) return "Unknown";
    if (client.company) return toProperCase(client.company.name);
    if (client.individual) return toProperCase(`${client.individual.firstName} ${client.individual.lastName}`);
    return "Unknown";
  };
  const getClientPhone = (client) => client?.company?.phoneNumber || client?.individual?.phoneNumber || "N/A";
  const getClientEmail = (client) => client?.company?.email || client?.individual?.email || "N/A";

  const getPaymentCount = (filterType) => {
    if (!stats) return 0;
    switch (filterType) {
      case "All": return stats.total || 0;
      case "Pending": return stats.payment?.Initialize || 0;
      case "Paid": return stats.payment?.Successful || 0;
      case "Unsuccessful": return stats.payment?.UnSuccessful || 0;
      default: return 0;
    }
  };

  const fetchGlobals = async () => {
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statsRes, lookupRes, locRes, catRes] = await Promise.all([
        fetch(`${BASE_URL}/admin/payment/stats`, { headers }),
        fetch(`${BASE_URL}/admin/ad/lookup?all=true`, { headers }),
        fetch(`${BASE_URL}/utils/ad/location?all=true`, { headers }),
        fetch(`${BASE_URL}/utils/ad/category?all=true`, { headers }),
      ]);
      const [statsData, lookupData, locData, catData] = await Promise.all([
        statsRes.json(), lookupRes.json(), locRes.json(), catRes.json(),
      ]);
      if (statsData.ok) setStats(statsData.data);
      if (lookupData.ok) setAllLookups(lookupData.data.pageData || []);
      if (locData.ok) setAllLocations(locData.data.pageData || []);
      if (catData.ok) setAllCategories(catData.data.pageData || []);
    } catch (err) {
      console.error("fetchGlobals error:", err);
    }
  };

  const fetchTypesForCategory = async (categoryId) => {
    if (!categoryId) { setEditTypes([]); return; }
    try {
      const token = getToken();
      const res = await fetch(`${BASE_URL}/utils/ad/category/${categoryId}/type?all=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.ok) setEditTypes(result.data.pageData || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (!editForm.zoneName || !editForm.categoryName || !editForm.typeName) {
      setEditAvailableSizes([]);
      return;
    }
    const filtered = allLookups.filter(l =>
      normalize(l.location?.name) === normalize(editForm.zoneName) &&
      normalize(l.category?.name) === normalize(editForm.categoryName) &&
      normalize(l.type?.name) === normalize(editForm.typeName)
    );
    const unique = [...new Map(filtered.map(l => [normalize(l.size?.name), l.size?.name])).entries()]
      .filter(([k]) => k).map(([, v]) => v);
    setEditAvailableSizes(unique);

    if (editForm.sizeName && !unique.some(s => normalize(s) === normalize(editForm.sizeName))) {
      setEditForm(prev => ({ ...prev, sizeName: "", price: 0, lookUpId: "" }));
    }
  }, [editForm.zoneName, editForm.categoryName, editForm.typeName, allLookups]);

  useEffect(() => {
    if (!showEditModal) return;
    if (!editForm.zoneName || !editForm.categoryName || !editForm.typeName || !editForm.sizeName) return;

    const match = allLookups.find(l =>
      normalize(l.location?.name) === normalize(editForm.zoneName) &&
      normalize(l.category?.name) === normalize(editForm.categoryName) &&
      normalize(l.type?.name) === normalize(editForm.typeName) &&
      normalize(l.size?.name) === normalize(editForm.sizeName)
    );

    if (match) {
      const total = (match.price || 0) * Number(editForm.measurement) * Number(editForm.time);
      setEditForm(prev => ({ ...prev, price: total, lookUpId: match.id }));
    } else {
      setEditForm(prev => ({ ...prev, price: 0, lookUpId: "" }));
    }
  }, [editForm.zoneName, editForm.categoryName, editForm.typeName, editForm.sizeName,
      editForm.measurement, editForm.time, allLookups, showEditModal]);

  const fetchPayments = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const token = getToken();
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("count", ITEMS_PER_PAGE);
      if (searchTerm) params.append("search", searchTerm);
      if (paymentFilter !== "All") {
        if (paymentFilter === "Paid") params.append("status", "Successful");
        if (paymentFilter === "Pending") params.append("status", "Initialize");
        if (paymentFilter === "Unsuccessful") params.append("status", "UnSuccessful");
      }
      const res = await fetch(`${BASE_URL}/admin/payment?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.ok) {
        let data = result.data.pageData || [];
        const total = result.data.totalItems || 0;

        if (filters.adType)
          data = data.filter(p => p.ad?.lookup?.type?.name === filters.adType);

        data.sort((a, b) => {
          let vA = "", vB = "";
          const s = (x) => (x || "").toString().toLowerCase();
          switch (sortConfig.key) {
            case "businessName": vA = s(getClientName(a.ad?.client)); vB = s(getClientName(b.ad?.client)); break;
            case "code": vA = s(a.code); vB = s(b.code); break;
            case "amount": vA = a.amount || 0; vB = b.amount || 0; break;
            case "status": vA = s(a.status); vB = s(b.status); break;
            default: vA = a.createdAt ? new Date(a.createdAt).getTime() : 0; vB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          }
          if (sortConfig.key === "amount" || sortConfig.key === "createdAt")
            return sortConfig.direction === "asc" ? vA - vB : vB - vA;
          return sortConfig.direction === "asc" ? (vA < vB ? -1 : vA > vB ? 1 : 0) : (vA > vB ? -1 : vA < vB ? 1 : 0);
        });

        setPayments(data);
        setTotalItems(total);
        setTotalPages(Math.ceil(total / ITEMS_PER_PAGE));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [searchTerm, paymentFilter, filters, sortConfig]);

  useEffect(() => { fetchGlobals(); }, []);
  useEffect(() => {
    const t = setTimeout(() => fetchPayments(currentPage), 400);
    return () => clearTimeout(t);
  }, [fetchPayments, currentPage]);

  const handleEditClick = async (payment) => {
    const ad = payment.ad;
    if (!ad) return alert("No advertisement data found.");

    const currentCategoryId = allCategories.find(
      c => normalize(c.name) === normalize(ad.lookup?.category?.name)
    )?.id || "";

    const currentZoneId = allLocations.find(
      l => normalize(l.name) === normalize(ad.lookup?.location?.name)
    )?.id || "";

    if (currentCategoryId) {
      await fetchTypesForCategory(currentCategoryId);
    }

    setEditForm({
      id: ad.id,
      clientId: ad.client?.id || "",
      title: ad.title || "",
      description: ad.description || "",
      address: ad.address || "",
      price: ad.price || 0,
      status: ad.status || "",
      measurement: ad.measurement || 1,
      time: ad.time || 1,
      lookUpId: ad.lookup?.id || "",
      zoneId: currentZoneId,
      zoneName: ad.lookup?.location?.name || "",
      categoryId: currentCategoryId,
      categoryName: ad.lookup?.category?.name || "",
      typeId: "",
      typeName: ad.lookup?.type?.name || "",
      sizeName: ad.lookup?.size?.name || "",
      location: {
        lga: ad.location?.lga || "",
        longitude: ad.location?.longitude || 0,
        latitude: ad.location?.latitude || 0,
        altitude: ad.location?.altitude || 0,
      },
      client: {
        type: ad.client?.type || "Individual",
        company: {
          name: ad.client?.company?.name || "",
          address: ad.client?.company?.address || "",
          phoneNumber: ad.client?.company?.phoneNumber || "",
          email: ad.client?.company?.email || "",
          type: ad.client?.company?.type || "Others",
        },
        individual: {
          firstName: ad.client?.individual?.firstName || "",
          lastName: ad.client?.individual?.lastName || "",
          gender: ad.client?.individual?.gender || "Male",
          phoneNumber: ad.client?.individual?.phoneNumber || "",
          email: ad.client?.individual?.email || "",
        },
      },
      imageFile: null,
      imagePreview: ad.imageUrl || null,
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "imageFile") {
      if (files && files[0]) {
        const file = files[0];
        const reader = new FileReader();
        reader.onloadend = () => setEditForm(prev => ({ ...prev, imageFile: file, imagePreview: reader.result }));
        reader.readAsDataURL(file);
      }
      return;
    }

    if (name === "zoneId") {
      const zone = allLocations.find(l => l.id === value);
      setEditForm(prev => ({
        ...prev, zoneId: value, zoneName: zone?.name || "",
        sizeName: "", price: 0, lookUpId: "",
      }));
      return;
    }

    if (name === "categoryId") {
      const cat = allCategories.find(c => c.id === value);
      setEditForm(prev => ({
        ...prev, categoryId: value, categoryName: cat?.name || "",
        typeId: "", typeName: "", sizeName: "", price: 0, lookUpId: "",
      }));
      fetchTypesForCategory(value);
      return;
    }

    if (name === "typeId") {
      const t = editTypes.find(t => t.id === value);
      setEditForm(prev => ({
        ...prev, typeId: value, typeName: t?.name || "",
        sizeName: "", price: 0, lookUpId: "",
      }));
      return;
    }

    if (name === "sizeName") {
      setEditForm(prev => ({ ...prev, sizeName: value }));
      return;
    }

    setEditForm(prev => {
      const newState = JSON.parse(JSON.stringify(prev));
      const keys = name.split(".");
      let cur = newState;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!cur[keys[i]]) cur[keys[i]] = {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return newState;
    });
  };

  const handleSaveAd = async () => {
    if (!editForm.lookUpId) return alert("Please select a valid Zone → Category → Type → Size combination to get pricing.");
    setSaving(true);
    try {
      const token = getToken();

      if (editForm.clientId) {
        const isCompany = editForm.client.type === "Company";
        const clientPayload = isCompany
          ? { name: editForm.client.company.name, address: editForm.client.company.address, email: editForm.client.company.email, phoneNumber: editForm.client.company.phoneNumber, type: editForm.client.company.type || "Others" }
          : { firstName: editForm.client.individual.firstName, lastName: editForm.client.individual.lastName, email: editForm.client.individual.email, phoneNumber: editForm.client.individual.phoneNumber, gender: editForm.client.individual.gender || "Male" };
        const endpoint = isCompany
          ? `${BASE_URL}/admin/client/${editForm.clientId}/company`
          : `${BASE_URL}/admin/client/${editForm.clientId}/individual`;
        const clientRes = await fetch(endpoint, {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(clientPayload),
        });
        const clientResult = await clientRes.json();
        if (!clientResult.ok) {
          alert(`Failed to update client: ${clientResult.error?.message || "Unknown error"}`);
          setSaving(false);
          return;
        }
      }

      const formData = new FormData();
      formData.append("title", editForm.title);
      formData.append("description", editForm.description);
      formData.append("address", editForm.address);
      formData.append("clientId", editForm.clientId);
      formData.append("time", editForm.time.toString());
      formData.append("measurement", editForm.measurement.toString());
      formData.append("lga", editForm.location.lga);
      formData.append("latitude", editForm.location.latitude.toString());
      formData.append("longitude", editForm.location.longitude.toString());
      formData.append("altitude", editForm.location.altitude.toString());
      formData.append("lookUpId", editForm.lookUpId);
      if (editForm.imageFile) formData.append("image", editForm.imageFile);

      const res = await fetch(`${BASE_URL}/admin/ad/${editForm.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await res.json();
      if (result.ok) {
        await fetchPayments(currentPage);
        setShowEditModal(false);
        alert("Advertisement updated successfully!");
      } else {
        alert(result.error?.message || "Failed to update advertisement");
      }
    } catch (err) {
      console.error(err);
      alert("An error occurred while saving.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAd = async () => {
    setDeleting(true);
    try {
      const token = getToken();
      const res = await fetch(`${BASE_URL}/admin/ad`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ adsId: [editForm.id] }),
      });
      const result = await res.json();
      if (result.ok) {
        await fetchPayments(currentPage);
        setShowEditModal(false);
        setShowDeleteConfirm(false);
        alert("Advertisement deleted successfully!");
      } else {
        alert(result.error?.message || "Failed to delete");
      }
    } catch (err) {
      console.error(err);
      alert("An error occurred.");
    } finally {
      setDeleting(false);
    }
  };

  const handleViewClick = (payment) => {
    const ad = payment.ad || {};
    setViewData({
      clientName: getClientName(ad.client),
      clientPhone: getClientPhone(ad.client),
      clientEmail: getClientEmail(ad.client),
      clientAddress: ad.client?.company?.address || "N/A",
      invoiceCode: payment.code,
      amount: payment.amount,
      status: payment.status,
      date: payment.createdAt,
      adTitle: ad.title,
      adType: ad.lookup?.type?.name,
      adCategory: ad.lookup?.category?.name,
      adSize: ad.lookup?.size?.name,
      adZone: ad.lookup?.location?.name,
      adAddress: ad.address,
      imageUrl: ad.imageUrl,
    });
    setShowViewModal(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = getToken();
      const res = await fetch(`${BASE_URL}/admin/payment?all=true`, { headers: { Authorization: `Bearer ${token}` } });
      const result = await res.json();
      if (!result.ok) throw new Error("Failed to fetch");
      const all = result.data.pageData || [];
      if (!all.length) { alert("No data to export."); return; }
      const rows = all.map(p => ({
        "Business Name": getClientName(p.ad?.client),
        "Invoice Code": p.code,
        "Ad Type": toProperCase(p.ad?.lookup?.type?.name || "-"),
        "Party Type": toProperCase(p.ad?.lookup?.category?.name || "-"),
        Zone: toProperCase(p.ad?.lookup?.location?.name || "-"),
        Faces: p.ad?.measurement || 1,
        Amount: p.amount || 0,
        Status: p.status === "Successful" ? "Paid" : p.status === "UnSuccessful" ? "Unsuccessful" : p.status,
        "Date Created": formatDate(p.createdAt),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Ads Log");
      XLSX.writeFile(wb, `EditAd_Log_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error(err);
      alert("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const renderSortIcon = (k) => {
    if (sortConfig.key !== k) return <i className="ki-outline ki-arrow-up-down fs-7 ms-1 text-muted"></i>;
    return sortConfig.direction === "asc"
      ? <i className="ki-outline ki-arrow-up fs-7 ms-1 text-primary"></i>
      : <i className="ki-outline ki-arrow-down fs-7 ms-1 text-primary"></i>;
  };
  const handleSort = (k) => setSortConfig(prev => ({ key: k, direction: prev.key === k && prev.direction === "asc" ? "desc" : "asc" }));
  const goToPage = (p) => { if (p >= 1 && p <= totalPages) setCurrentPage(p); };

  const renderSkeletons = () => Array.from({ length: 10 }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: 11 }).map((__, j) => (
        <td key={j}><div className="skeleton-box" style={{ width: j === 0 ? "80%" : "60%" }}></div>{" "}</td>
      ))}
    </tr>
  ));

  const uniqueZones = useMemo(() =>
    [...new Map(allLocations.map(l => [l.id, l])).values()],
    [allLocations]
  );
  const uniqueCategories = useMemo(() =>
    [...new Map(allCategories.map(c => [c.id, c])).values()],
    [allCategories]
  );

  return (
    <>
      <style>{skeletonStyles}</style>
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">

          <div id="kt_app_header" className="app-header">
            <div className="app-container container-fluid d-flex align-items-stretch justify-content-between">
              <div className="app-header-logo d-flex align-items-center me-lg-20 gap-2">
                <Link to="/"><img alt="Logo" src="assets/aderalogo.png" className="h-60px" /></Link>
              </div>
              <div className="d-flex align-items-stretch justify-content-between flex-lg-grow-1">
                <div className="d-flex align-items-stretch" id="kt_app_header_menu_wrapper">
                  <div className="app-header-menu app-header-mobile-drawer align-items-stretch">
                    <div className="menu menu-rounded menu-column menu-lg-row menu-active-bg menu-title-gray-600 fw-semibold fs-6 align-items-stretch my-5 px-2">
                      <div className="menu-item me-2"><Link to="/dashboarda" className="menu-link"><span className="menu-title">Home</span></Link></div>
                      <div className="menu-item me-2"><Link to="/ad-management" className="menu-link"><span className="menu-title">Advertisement</span></Link></div>
                      <div className="menu-item me-2"><Link to="/agents" className="menu-link"><span className="menu-title">Agents</span></Link></div>
                      <div className="menu-item here show menu-here-bg me-2"><Link to="/payments" className="menu-link"><span className="menu-title">Payments</span></Link></div>
                    </div>
                  </div>
                </div>
                <div className="app-navbar flex-shrink-0 d-flex align-items-center gap-3">
                  <div className="text-end d-none d-sm-flex flex-column justify-content-center me-3">
                    <span className="text-gray-500 fs-8 fw-bold">Hello</span>
                    <span className="text-gray-800 fs-7 fw-bold">EDSAA Admin</span>
                  </div>
                  <div className="cursor-pointer symbol symbol-circle symbol-40px">
                    <img src="assets/edsal.png" alt="User" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="app-wrapper d-flex flex-column flex-column-fluid" id="kt_app_wrapper">
            <div className="app-container container-fluid d-flex flex-row-fluid">
              <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
                <div className="d-flex flex-column flex-column-fluid">
                  <div id="kt_app_content" className="app-content flex-column-fluid">

                    <div className="d-flex flex-stack flex-wrap flex-md-nowrap card-rounded shadow p-5 p-lg-10 mb-10"
                      style={{ background: "linear-gradient(90deg, #20AA3E 0%, #03A588 100%)" }}>
                      <div className="my-2 me-5">
                        <div className="fs-2x fw-bold text-white mb-2">
                          <u><b>{stats?.total || 0}</b></u>{" "}
                          <span className="fw-normal">Transactions</span>
                        </div>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-4 my-1">
                            <li className="breadcrumb-item text-white ">Payments</li>
                            <li className="breadcrumb-item">
                            <span className="bullet bg-white w-4px h-4px"></span>
                            </li>
                            <li className="breadcrumb-item text-white ">Edit Advertisement</li>
                        </ul>
                      </div>
                    </div>

                    <div className="card">
                      <div className="card-header border-0 pt-6">
                        <div className="card-title">
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              setCurrentPage(1);
                              fetchPayments(1);
                            }}
                            className="d-flex align-items-center position-relative my-1"
                          >
                            <i className="ki-outline ki-magnifier fs-3 position-absolute ms-5"></i>
                            <input
                              type="text"
                              className="form-control w-400px ps-12"
                              placeholder="Search advertisment..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </form>
                        </div>
                        <div className="card-toolbar">
                          <div className="d-flex justify-content-end flex-wrap gap-2">
                            <div className="d-flex flex-wrap align-items-center justify-content-between">
                              <div className="btn-group" role="group">
                                {["All", "Pending", "Unsuccessful", "Paid"].map((filter) => (
                                  <button
                                    key={filter}
                                    type="button"
                                    className={`btn ${paymentFilter === filter ? "btn-light-primary active" : "btn-light"}`}
                                    onClick={() => {
                                      setPaymentFilter(filter);
                                      setCurrentPage(1);
                                    }}
                                  >
                                    {filter} ({getPaymentCount(filter)})
                                  </button>
                                ))}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-light-primary"
                              onClick={handleExport}
                              disabled={exporting}
                            >
                              {exporting ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <>
                                  <i className="ki-outline ki-exit-up fs-2"></i> Export All
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="card-body pt-0">
                        <div className="table-responsive">
                          <div style={{ minHeight: "400px" }}>
                            <table className="table align-middle table-row-dashed fs-6 gy-5">
                              <thead>
                                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0 sticky-top bg-white">
                                  <th className="min-w-250px cursor-pointer" onClick={() => handleSort("businessName")}>Business Details {renderSortIcon("businessName")}</th>
                                  <th className="min-w-150px cursor-pointer" onClick={() => handleSort("code")}>Invoice {renderSortIcon("code")}</th>
                                  <th className="min-w-125px cursor-pointer" onClick={() => handleSort("adType")}>Ad Type {renderSortIcon("adType")}</th>
                                  <th className="min-w-125px">Party Type</th>
                                  <th className="min-w-125px">Zone</th>
                                  <th className="min-w-80px">Faces</th>
                                  <th className="min-w-125px cursor-pointer" onClick={() => handleSort("amount")}>Amount {renderSortIcon("amount")}</th>
                                  <th className="min-w-125px cursor-pointer" onClick={() => handleSort("status")}>Status {renderSortIcon("status")}</th>
                                  <th className="min-w-150px cursor-pointer" onClick={() => handleSort("createdAt")}>Date {renderSortIcon("createdAt")}</th>
                                  <th className="text-end min-w-200px">Actions</th>
                                 </tr>
                              </thead>
                              <tbody className="text-gray-600 fw-semibold">
                                {loading ? renderSkeletons() : (
                                  <>
                                    {payments.length === 0 && (
                                      <tr><td colSpan="10" className="text-center py-6 text-muted">No records found</td></tr>
                                    )}
                                    {payments.map(payment => (
                                      <tr key={payment.id}>
                                        <td>
                                          <div className="d-flex align-items-center">
                                            <div className="symbol symbol-50px me-3">
                                              <img src={payment.ad?.imageUrl || "assets/bill2.jpg"} alt="Ad"
                                                className="rounded" style={{ width: 50, height: 50, objectFit: "cover" }}
                                                onError={e => e.target.src = "assets/bill2.jpg"} />
                                            </div>
                                            <div className="d-flex flex-column">
                                              <span className="text-gray-800 fw-bold fs-6 mb-1">{getClientName(payment.ad?.client)}</span>
                                              <span className="text-muted fs-7">{getClientEmail(payment.ad?.client)?.toLowerCase()}</span>
                                              <span className="text-muted fs-7">{getClientPhone(payment.ad?.client)}</span>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="fw-bold">{payment.code}</td>
                                        <td>{toProperCase(payment.ad?.lookup?.type?.name || "-")}</td>
                                        <td>{toProperCase(payment.ad?.lookup?.category?.name || "-")}</td>
                                        <td>{toProperCase(payment.ad?.lookup?.location?.name || "-")}</td>
                                        <td>{payment.ad?.measurement || 1}</td>
                                        <td>{formatCurrency(payment.amount)}</td>
                                        <td>
                                          <span className={`badge ${payment.status === "Successful" ? "badge-light-success" : payment.status === "Initialize" || payment.status === "AwaitingPayment" ? "badge-light-warning" : "badge-light-danger"}`}>
                                            {payment.status === "Successful" ? "Paid" : payment.status === "Initialize" ? "Pending" : payment.status === "UnSuccessful" ? "Unsuccessful" : payment.status}
                                          </span>
                                        </td>
                                        <td>{formatDate(payment.createdAt)}</td>
                                        <td className="text-end">
                                          {payment.status === "Successful" && (
                                            <button className="btn btn-sm btn-light-success me-2" onClick={() => {
                                              const agentName = payment.ad?.agent
                                                ? `${payment.ad.agent.firstName || ""} ${payment.ad.agent.lastName || ""}`.trim()
                                                : "Unassigned Agent";
                                              setPermitData({
                                                clientName: toProperCase(getClientName(payment.ad?.client)),
                                                clientAddress: toProperCase(payment.ad?.client?.company?.address || "N/A"),
                                                clientContact: getClientPhone(payment.ad?.client),
                                                amount: formatCurrency(payment.amount),
                                                category: toProperCase(payment.ad?.lookup?.category?.name || "N/A"),
                                                type: toProperCase(payment.ad?.lookup?.type?.name || "N/A"),
                                                expiryDate: getExpiryDate(payment.paidAt),
                                                adTitle: toProperCase(payment.ad?.title || ""),
                                                adSize: toProperCase(payment.ad?.lookup?.size?.name || "N/A"),
                                                adLocation: toProperCase(payment.ad?.location?.lga || "N/A"),
                                                adAddress: toProperCase(payment.ad?.address || "N/A"),
                                                imageUrl: payment.ad?.imageUrl || "assets/bill2.jpg",
                                                status: "Paid",
                                                paidAt: payment.paidAt,
                                                agentName: toProperCase(agentName),
                                                agentEmail: payment.ad?.agent?.email || "N/A",
                                                agentPhone: payment.ad?.agent?.phoneNumber || "N/A",
                                              });
                                              setShowPermitModal(true);
                                            }}>View Permit</button>
                                          )}
                                          {payment.status !== "Successful" && payment.status !== "UnSuccessful" && (
                                            <button className="btn btn-sm btn-light-warning me-2" onClick={() => handleEditClick(payment)}>Edit Ad</button>
                                          )}
                                          <button className="btn btn-sm btn-light-success" onClick={() => handleViewClick(payment)}>View Details</button>
                                        </td>
                                      </tr>
                                    ))}
                                  </>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {!loading && payments.length > 0 && (
                          <div className="d-flex justify-content-between align-items-center pt-4">
                            <div className="text-muted fs-7">
                              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems} records
                            </div>
                            <div>
                              <button className="btn btn-sm btn-light me-2" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>Previous</button>
                              <span className="fw-bold mx-2 text-gray-600">Page {currentPage} of {totalPages}</span>
                              <button className="btn btn-sm btn-light" disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>Next</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="app-footer py-10 mt-10 border-top">
                      <div className="text-center">
                        <span className="text-muted fw-semibold me-1">2026©</span>
                        <a href="https://edsaa.com.ng/" target="_blank" rel="noopener noreferrer" className="text-gray-800 text-hover-primary fw-bold">EDSAA - ADERA</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showEditModal && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered mw-900px modal-dialog-scrollable">
              <div className="modal-content rounded shadow-lg">
                <div className="modal-header border-0 pb-0">
                  <h3 className="modal-title fw-bold">Edit Advertisement</h3>
                  <button className="btn-close" onClick={() => setShowEditModal(false)} disabled={saving}></button>
                </div>
                <div className="modal-body py-5 px-lg-10">
                  <div className="row g-5">
                    <div className="col-md-6 mb-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Basic Information</h6>
                      <div className="bg-light p-4 rounded border h-100">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Title <small className="text-muted">(auto-generated)</small></label>
                          <input type="text" className="form-control" name="title" value={editForm.title} onChange={handleEditFormChange} />
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Description</label>
                          <textarea className="form-control" name="description" value={editForm.description} onChange={handleEditFormChange} rows="3" />
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Ad Image</label>
                          <div className="d-flex align-items-center gap-3">
                            {editForm.imagePreview && (
                              <div className="symbol symbol-60px border rounded cursor-pointer" onClick={() => { setLightboxImage(editForm.imagePreview); setShowLightbox(true); }}>
                                <img src={editForm.imagePreview} alt="Preview" className="object-fit-cover" onError={e => e.target.style.display = "none"} />
                              </div>
                            )}
                            <input type="file" className="form-control" name="imageFile" accept="image/*" onChange={handleEditFormChange} />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold required">Address</label>
                          <textarea className="form-control" name="address" value={editForm.address} onChange={handleEditFormChange} rows="2" />
                        </div>
                        <div>
                          <label className="form-label fw-semibold">Status</label>
                          <input type="text" className="form-control" value={editForm.status} readOnly />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 mb-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Advertisement Details</h6>
                      <div className="bg-light p-4 rounded border h-100">
                        <div className="mb-3">
                          <label className="form-label fw-semibold required">Zone (Location)</label>
                          <select className="form-select" name="zoneId" value={editForm.zoneId} onChange={handleEditFormChange}>
                            <option value="">Select Zone</option>
                            {uniqueZones.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                          </select>
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-semibold required">Category</label>
                          <select className="form-select" name="categoryId" value={editForm.categoryId} onChange={handleEditFormChange} disabled={!editForm.zoneId}>
                            <option value="">Select Category</option>
                            {uniqueCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-semibold required">Type</label>
                          <select className="form-select" name="typeId" value={editForm.typeId} onChange={handleEditFormChange} disabled={!editForm.categoryId}>
                            <option value="">Select Type</option>
                            {editTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                          {editForm.categoryId && editTypes.length === 0 && <div className="text-warning small mt-1">No types found for this category.</div>}
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-semibold required">Size</label>
                          <select className="form-select" name="sizeName" value={editForm.sizeName} onChange={handleEditFormChange} disabled={!editForm.typeId}>
                            <option value="">Select Size</option>
                            {editAvailableSizes.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          {editForm.typeId && editAvailableSizes.length === 0 && <div className="text-warning small mt-1">No sizes found for this combination.</div>}
                        </div>

                        <div className="row mb-3">
                          <div className="col-6">
                            <label className="form-label fw-semibold">No. of Faces</label>
                            <input type="number" className="form-control" name="measurement" value={editForm.measurement} onChange={handleEditFormChange} min="1" />
                          </div>
                          <div className="col-6">
                            <label className="form-label fw-semibold">Duration (Years)</label>
                            <input type="number" className="form-control" name="time" value={editForm.time} onChange={handleEditFormChange} min="1" />
                          </div>
                        </div>

                        <div className={`p-3 rounded ${editForm.lookUpId ? "bg-success bg-opacity-10 border border-success" : "bg-warning bg-opacity-10 border border-warning"}`}>
                          {editForm.lookUpId
                            ? <div className="text-success fw-bold fs-5"><i className="ki-outline ki-check-circle me-2"></i>Total: {formatCurrency(editForm.price)}</div>
                            : <div className="text-warning fw-semibold fs-7"><i className="ki-outline ki-information me-2"></i>Select Zone → Category → Type → Size to calculate price</div>
                          }
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 mb-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Client Information</h6>
                      <div className="bg-light p-4 rounded border h-100">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Client Type</label>
                          <input type="text" className="form-control" value={editForm.client.type} readOnly />
                        </div>
                        {editForm.client.type === "Company" ? (
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold">Company Name</label>
                              <input type="text" className="form-control" name="client.company.name" value={editForm.client.company.name} onChange={handleEditFormChange} />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold">Email</label>
                              <input type="email" className="form-control" name="client.company.email" value={editForm.client.company.email} onChange={handleEditFormChange} />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold">Phone</label>
                              <input type="text" className="form-control" name="client.company.phoneNumber" value={editForm.client.company.phoneNumber} onChange={handleEditFormChange} />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold">Company Type</label>
                              <select className="form-select" name="client.company.type" value={editForm.client.company.type} onChange={handleEditFormChange}>
                                <option value="Others">Others</option>
                                <option value="Corporation">Corporation</option>
                                <option value="LLC">LLC</option>
                                <option value="Partnership">Partnership</option>
                              </select>
                            </div>
                            <div className="col-12 mb-3">
                              <label className="form-label fw-semibold">Address</label>
                              <textarea className="form-control" name="client.company.address" value={editForm.client.company.address} onChange={handleEditFormChange} rows="2" />
                            </div>
                          </div>
                        ) : (
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold">First Name</label>
                              <input type="text" className="form-control" name="client.individual.firstName" value={editForm.client.individual.firstName} onChange={handleEditFormChange} />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold">Last Name</label>
                              <input type="text" className="form-control" name="client.individual.lastName" value={editForm.client.individual.lastName} onChange={handleEditFormChange} />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold">Email</label>
                              <input type="email" className="form-control" name="client.individual.email" value={editForm.client.individual.email} onChange={handleEditFormChange} />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold">Phone</label>
                              <input type="text" className="form-control" name="client.individual.phoneNumber" value={editForm.client.individual.phoneNumber} onChange={handleEditFormChange} />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold">Gender</label>
                              <select className="form-select" name="client.individual.gender" value={editForm.client.individual.gender} onChange={handleEditFormChange}>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 mb-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Location Information</h6>
                      <div className="bg-light p-4 rounded border h-100">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">LGA</label>
                          <input type="text" className="form-control" name="location.lga" value={editForm.location.lga} onChange={handleEditFormChange} />
                        </div>
                        <div className="row">
                          <div className="col-6 mb-2">
                            <label className="form-label fw-semibold small">Latitude</label>
                            <input type="number" step="any" className="form-control" name="location.latitude" value={editForm.location.latitude} onChange={handleEditFormChange} />
                          </div>
                          <div className="col-6 mb-2">
                            <label className="form-label fw-semibold small">Longitude</label>
                            <input type="number" step="any" className="form-control" name="location.longitude" value={editForm.location.longitude} onChange={handleEditFormChange} />
                          </div>
                          <div className="col-12">
                            <label className="form-label fw-semibold small">Altitude</label>
                            <input type="number" step="any" className="form-control" name="location.altitude" value={editForm.location.altitude} onChange={handleEditFormChange} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between mt-8">
                    <button className="btn btn-danger btn-lg px-5" onClick={() => setShowDeleteConfirm(true)} disabled={deleting || saving}>
                      Delete Advertisement
                    </button>
                    <div className="d-flex gap-3">
                      <button className="btn btn-light btn-lg px-5" onClick={() => setShowEditModal(false)} disabled={saving}>Cancel</button>
                      <button className="btn btn-primary btn-lg px-5" onClick={handleSaveAd} disabled={saving || deleting || !editForm.lookUpId}>
                        {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</> : "Save Changes"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-danger">Confirm Deletion</h5>
                  <button className="btn-close" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete this advertisement? This cannot be undone.</p>
                  <p className="text-muted">Title: <strong>{editForm.title}</strong></p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</button>
                  <button className="btn btn-danger" onClick={handleDeleteAd} disabled={deleting}>
                    {deleting ? <><span className="spinner-border spinner-border-sm me-2"></span>Deleting...</> : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPermitModal && permitData && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered mw-900px">
              <div className="modal-content rounded shadow-lg">
                <div className="modal-header border-0 pb-0 position-relative">
                  <div className="w-100 text-center mb-2">
                    <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                      <img src="assets/edo.png" alt="Edo" className="h-60px" />
                      <img src="assets/edsal.png" alt="EDSAL" className="h-60px" />
                    </div>
                    <h3 className="fw-bold text-uppercase text-success mb-1">Edo State Signage And Advertisement Agency</h3>
                    <h4 className="fw-bold text-uppercase text-success mb-0">Advertising Permit</h4>
                  </div>
                  <button className="btn btn-sm btn-icon position-absolute top-0 end-0 m-3" onClick={() => setShowPermitModal(false)}>
                    <i className="ki-outline ki-cross fs-1"></i>
                  </button>
                </div>
                <div className="modal-body py-5 px-lg-10">
                  <div className="d-flex flex-column align-items-center mb-8">
                    <div className="rounded overflow-hidden shadow-sm border w-100 bg-light d-flex justify-content-center align-items-center" style={{ minHeight: 250 }}>
                      <img src={permitData.imageUrl} alt="Ad" className="img-fluid img-zoom-hover"
                        style={{ maxHeight: 350, objectFit: "contain" }}
                        onError={e => e.target.src = "assets/bill2.jpg"}
                        onClick={() => { setLightboxImage(permitData.imageUrl); setShowLightbox(true); }} />
                    </div>
                  </div>
                  <div className="row g-5">
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Client Information</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2"><strong>Name:</strong> {permitData.clientName}</div>
                        <div className="mb-2"><strong>Address:</strong> {permitData.clientAddress}</div>
                        <div><strong>Contact:</strong> {permitData.clientContact}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Permit Details</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2"><strong>Amount:</strong> {permitData.amount}</div>
                        <div className="mb-2"><strong>Category:</strong> {permitData.category}</div>
                        <div className="mb-2"><strong>Type:</strong> {permitData.type}</div>
                        <div><strong>Expiry:</strong> {permitData.expiryDate}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Ad Details</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2"><strong>Title:</strong> {permitData.adTitle}</div>
                        <div className="mb-2"><strong>Size:</strong> {permitData.adSize}</div>
                        <div className="mb-2"><strong>Zone:</strong> {permitData.adLocation}</div>
                        <div><strong>Address:</strong> {permitData.adAddress}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Agent Information</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2"><strong>Name:</strong> {permitData.agentName}</div>
                        <div className="mb-2"><strong>Email:</strong> {permitData.agentEmail}</div>
                        <div><strong>Phone:</strong> {permitData.agentPhone}</div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-8">
                    <button className="btn btn-success btn-lg px-5">Download Permit</button>
                    <button className="btn btn-light btn-lg px-5" onClick={() => setShowPermitModal(false)}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showViewModal && viewData && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered mw-800px">
              <div className="modal-content rounded shadow-lg border-0">
                <div className="modal-header pb-0 border-0">
                  <h3 className="modal-title fw-bold">Advertisement Details</h3>
                  <button className="btn-close" onClick={() => setShowViewModal(false)}></button>
                </div>
                <div className="modal-body py-5 px-lg-10">
                  <div className="d-flex justify-content-center mb-5">
                    <img src={viewData.imageUrl} alt="Ad" className="img-fluid rounded img-zoom-hover"
                      style={{ maxHeight: 250, cursor: "pointer" }}
                      onError={e => e.target.src = "assets/bill2.jpg"}
                      onClick={() => { setLightboxImage(viewData.imageUrl); setShowLightbox(true); }} />
                  </div>
                  <div className="row g-5">
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Payment Info</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2"><strong>Invoice:</strong> {viewData.invoiceCode}</div>
                        <div className="mb-2"><strong>Amount:</strong> {formatCurrency(viewData.amount)}</div>
                        <div className="mb-2"><strong>Status:</strong> <span className={`badge ms-1 ${viewData.status === "Successful" ? "badge-light-success" : "badge-light-warning"}`}>{viewData.status}</span></div>
                        <div><strong>Date:</strong> {formatDate(viewData.date)}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Client Info</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2"><strong>Name:</strong> {toProperCase(viewData.clientName)}</div>
                        <div className="mb-2"><strong>Phone:</strong> {viewData.clientPhone}</div>
                        <div><strong>Email:</strong> {viewData.clientEmail}</div>
                      </div>
                    </div>
                    <div className="col-12">
                      <h6 className="fw-bold text-gray-700 mb-3">Ad Info</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="row">
                          <div className="col-md-6 mb-2"><strong>Title:</strong> {toProperCase(viewData.adTitle)}</div>
                          <div className="col-md-6 mb-2"><strong>Zone:</strong> {toProperCase(viewData.adZone)}</div>
                          <div className="col-md-6 mb-2"><strong>Type:</strong> {toProperCase(viewData.adType)}</div>
                          <div className="col-md-6 mb-2"><strong>Size:</strong> {viewData.adSize}</div>
                          <div className="col-12"><strong>Address:</strong> {toProperCase(viewData.adAddress)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-5">
                    <button className="btn btn-light" onClick={() => setShowViewModal(false)}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showLightbox && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}
            onClick={() => setShowLightbox(false)}>
            <button className="btn btn-icon position-absolute top-0 end-0 m-4" onClick={() => setShowLightbox(false)}>
              <i className="ki-outline ki-cross fs-2x text-white"></i>
            </button>
            <img src={lightboxImage} alt="Full" style={{ maxHeight: "90vh", maxWidth: "90vw", objectFit: "contain", borderRadius: 8 }}
              onError={e => e.target.src = "assets/bill2.jpg"} />
          </div>
        )}
      </div>
    </>
  );
};

export default EditAd;